import {
	useMultiSelectField,
	type MultiSelectFieldValue,
} from '@atlassian/jira-issue-field-select';
import { MULTI_SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { useGenericClipboardHandler } from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import { multiSelectSupportedTypes } from '../supported-field-types';
import type { TransformedMultiSelectOption } from '../types';

const isTransformedMultiSelectOption = (option: unknown): option is TransformedMultiSelectOption =>
	!!option && typeof option === 'object' && 'optionId' in option && 'value' in option;

const isTransformedMultiSelect = (
	fieldValue: unknown,
): fieldValue is TransformedMultiSelectOption[] =>
	Array.isArray(fieldValue) && fieldValue.every(isTransformedMultiSelectOption);

const emptyValue: [] = [];

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): MultiSelectFieldValue => {
	if (
		!clipboardItem ||
		!metadata?.fieldType ||
		!metadata?.fieldValue ||
		!isTransformedMultiSelect(metadata?.fieldValue)
	) {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	if (!(multiSelectSupportedTypes as string[]).includes(metadata.fieldType)) {
		return null;
	}

	return metadata?.fieldValue.filter(Boolean).map(({ optionId, value }) => ({
		id: optionId,
		value,
	}));
};

const useMultiSelectClipboardHandler: PasteHandler = (
	issue: { key: string; issueId: string },
	field: ListIssueFieldTransformed,
) => {
	const [, { saveValue }] = useMultiSelectField({
		fieldKey: field.fieldId,
		issueKey: issue.key,
		fieldType: MULTI_SELECT_CF_TYPE,
	});

	return useGenericClipboardHandler(field, getPastedValue, saveValue, emptyValue);
};

const MultiSelectClipboardHelper = createClipboardHelperComponent(useMultiSelectClipboardHandler);

export const multiSelectClipboardHandler = {
	[MULTI_SELECT_CF_TYPE]: MultiSelectClipboardHelper,
};
